/**
 * Rule Book
 *
 * Where the finance team writes the rules every contract analysis is checked
 * against. Rules go live as soon as they are saved; each change is versioned.
 */

import React, { useMemo, useState } from 'react';
import { BookOpenCheck, FileUp, History, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  useRules,
  useCreateRule,
  useUpdateRule,
  useToggleRule,
  useDeleteRule,
  useRuleSuggestions
} from '../../../hooks/queries/useRules';
import { FinanceRule, FinanceRuleInput } from '../../../services/api/ruleService';
import { FinanceRuleSeverity } from '../../../types/contract.types';
import { ClassificationBadge } from '../contract-workflow/shared/PlaybookReviewPanel';
import { RuleFormModal } from './RuleFormModal';
import { RuleHistoryDrawer } from './RuleHistoryDrawer';
import { PlaybookThresholdsTab } from './PlaybookThresholdsTab';
import { SuggestionsTab } from './SuggestionsTab';
import { ImportRulesModal } from './ImportRulesModal';
import { SEVERITY_OPTIONS, contractTypeLabel, personName } from './ruleBookConstants';

const controlClass =
  'rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400';
// The forms plugin draws the dropdown arrow inside the right padding, so selects need room for it
const selectClass = `${controlClass} pl-3 pr-9`;

const Toggle: React.FC<{ on: boolean; disabled?: boolean; label: string; onChange: (on: boolean) => void }> = ({
  on, disabled, label, onChange
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      on ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
    }`}
  >
    <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

const FinanceRulesTab: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('rules.edit');

  const { data, isLoading, error } = useRules();
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const toggleRule = useToggleRule();
  const deleteRule = useDeleteRule();

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState<FinanceRuleSeverity | ''>('');
  const [status, setStatus] = useState<'all' | 'on' | 'off'>('all');

  const [editing, setEditing] = useState<FinanceRule | null | undefined>(undefined);
  const [historyFor, setHistoryFor] = useState<FinanceRule | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const rules = useMemo(() => data?.rules ?? [], [data]);
  const categories = data?.categories ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rules.filter(r =>
      (!q || r.title.toLowerCase().includes(q) || r.ruleText.toLowerCase().includes(q)) &&
      (!category || r.category === category) &&
      (!severity || r.severity === severity) &&
      (status === 'all' || (status === 'on') === r.enabled)
    );
  }, [rules, search, category, severity, status]);

  const enabledCount = rules.filter(r => r.enabled).length;
  const saving = createRule.isPending || updateRule.isPending;
  const formError = (createRule.error || updateRule.error)?.message ?? null;

  const openForm = (rule: FinanceRule | null) => {
    createRule.reset();
    updateRule.reset();
    setEditing(rule);
  };

  const save = (input: FinanceRuleInput) => {
    const done = { onSuccess: () => setEditing(undefined) };
    if (editing) updateRule.mutate({ id: editing._id, input }, done);
    else createRule.mutate(input, done);
  };

  const runAction = (p: Promise<unknown>) => {
    setActionError(null);
    p.catch((e: Error) => setActionError(e.message));
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search rules"
            aria-label="Search rules"
            className={`${controlClass} w-full pl-9 pr-3`}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select aria-label="Category" className={selectClass} value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select aria-label="Severity" className={selectClass} value={severity} onChange={e => setSeverity(e.target.value as FinanceRuleSeverity | '')}>
            <option value="">All severities</option>
            {SEVERITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select aria-label="Status" className={selectClass} value={status} onChange={e => setStatus(e.target.value as 'all' | 'on' | 'off')}>
            <option value="all">On and off</option>
            <option value="on">On</option>
            <option value="off">Off</option>
          </select>
          {canEdit && (
            <button
              onClick={() => setImporting(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-200 bg-white dark:bg-dark-bg-secondary hover:bg-gray-50 dark:hover:bg-dark-surface-raised"
            >
              <FileUp className="w-4 h-4" /> Import CSV
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => openForm(null)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white dark:bg-accent-500 dark:hover:bg-accent-400 dark:text-gray-900"
            >
              <Plus className="w-4 h-4" /> Add rule
            </button>
          )}
        </div>
      </div>

      <p className={`text-sm text-gray-500 dark:text-gray-400 ${isLoading || error ? 'invisible' : ''}`}>
        {enabledCount} of {rules.length} rule{rules.length === 1 ? '' : 's'} on. Every new analysis checks the contract
        against the rules that are on.
      </p>

      {actionError && (
        <p role="alert" className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {actionError}
        </p>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map(i => <div key={i} className="h-24 rounded-xl bg-gray-100 dark:bg-dark-bg-secondary animate-pulse" />)}
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Couldn't load the rule book: {(error as Error).message}</p>
      ) : rules.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-dark-border p-10 text-center">
          <BookOpenCheck className="w-10 h-10 mx-auto text-gray-400" />
          <h3 className="text-base mt-3 font-medium text-gray-900 dark:text-white">No rules yet</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Write the terms finance will and won't accept, for example "Payment terms longer than Net 60 are not
            acceptable". Every contract analysis will check against them.
          </p>
          {canEdit && (
            <button
              onClick={() => openForm(null)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white dark:bg-accent-500 dark:text-gray-900"
            >
              <Plus className="w-4 h-4" /> Add the first rule
            </button>
          )}
          {canEdit && (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Have a list already?{' '}
              <button onClick={() => setImporting(true)} className="font-medium text-primary-600 dark:text-accent-400 hover:underline">
                Import them from a CSV
              </button>
            </p>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No rules match these filters.</p>
      ) : (
        <ul className="space-y-3">
          {filtered.map(rule => (
            <li
              key={rule._id}
              className={`rounded-xl border bg-white dark:bg-dark-surface p-4 transition-opacity ${
                rule.enabled ? 'border-gray-200 dark:border-dark-border' : 'border-dashed border-gray-300 dark:border-dark-border opacity-70'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{rule.title}</h3>
                    <ClassificationBadge classification={rule.severity} />
                    <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-dark-bg-secondary text-xs text-gray-600 dark:text-gray-300">
                      {rule.category}
                    </span>
                    {!rule.enabled && <span className="text-xs font-medium text-gray-500">Off</span>}
                  </div>
                  <p className="mt-1.5 text-sm text-gray-700 dark:text-gray-300">{rule.ruleText}</p>
                  {rule.suggestedPosition && (
                    <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-medium">Our position:</span> {rule.suggestedPosition}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                    {rule.appliesTo?.length ? rule.appliesTo.map(contractTypeLabel).join(', ') : 'All contract types'}
                    {' '}&middot; v{rule.version} &middot; updated by {personName(rule.updatedBy || rule.createdBy)}{' '}
                    {new Date(rule.updatedAt).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex flex-col items-end gap-3 flex-shrink-0">
                  <Toggle
                    on={rule.enabled}
                    disabled={!canEdit || toggleRule.isPending}
                    label={`${rule.enabled ? 'Turn off' : 'Turn on'} ${rule.title}`}
                    onChange={on => runAction(toggleRule.mutateAsync({ id: rule._id, enabled: on }))}
                  />
                  <div className="flex items-center gap-1">
                    <button onClick={() => setHistoryFor(rule)} title="History" aria-label={`History of ${rule.title}`} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
                      <History className="w-4 h-4" />
                    </button>
                    {canEdit && (
                      <>
                        <button
                          onClick={() => openForm(rule)}
                          disabled={toggleRule.isPending && toggleRule.variables?.id === rule._id}
                          title="Edit"
                          aria-label={`Edit ${rule.title}`}
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary disabled:opacity-40"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {confirmDelete === rule._id ? (
                          <span className="flex items-center gap-1 text-xs">
                            <button
                              onClick={() => { setConfirmDelete(null); runAction(deleteRule.mutateAsync(rule._id)); }}
                              className="px-2 py-1 rounded-md bg-red-600 text-white font-medium"
                            >
                              Delete
                            </button>
                            <button onClick={() => setConfirmDelete(null)} className="px-2 py-1 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
                              Keep
                            </button>
                          </span>
                        ) : (
                          <button onClick={() => setConfirmDelete(rule._id)} title="Delete" aria-label={`Delete ${rule.title}`} className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing !== undefined && (
        <RuleFormModal
          rule={editing}
          categories={categories}
          saving={saving}
          error={formError}
          onSave={save}
          onClose={() => setEditing(undefined)}
        />
      )}
      {importing && <ImportRulesModal onClose={() => setImporting(false)} />}
      {historyFor && <RuleHistoryDrawer rule={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
};

const TABS = [
  {
    id: 'rules',
    label: 'Finance rules',
    intro: 'The rules finance holds every contract to. When a contract is analysed, the AI checks it against each ' +
      'rule that is on, quotes the clause it relied on, and flags breaches at the severity set here.'
  },
  {
    id: 'suggestions',
    label: 'Suggested rules',
    intro: 'Rules the AI proposes after spotting serious problems in analysed contracts that your rule book ' +
      "doesn't cover. Review each one: add it (editing it first if you like) or dismiss it so it isn't proposed again."
  },
  {
    id: 'thresholds',
    label: 'Playbook thresholds',
    intro: 'The benchmarks of the built-in review framework. Tune a threshold, its wording or its priority; the ' +
      'built-in value stays available as the default.'
  }
] as const;

type TabId = typeof TABS[number]['id'];

export const RuleBook: React.FC = () => {
  const [tab, setTab] = useState<TabId>('rules');
  const active = TABS.find(t => t.id === tab)!;
  const pendingSuggestions = useRuleSuggestions('pending').data?.pendingCount ?? 0;

  return (
    <div className="max-w-5xl">
      <div role="tablist" aria-label="Rule book" className="flex gap-1 border-b border-gray-200 dark:border-dark-border mb-5">
        {TABS.map(t => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? 'border-primary-600 text-primary-700 dark:border-accent-400 dark:text-accent-300'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {t.label}
            {t.id === 'suggestions' && pendingSuggestions > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary-600 text-white dark:bg-accent-500 dark:text-gray-900 text-xs font-semibold">
                {pendingSuggestions}
              </span>
            )}
          </button>
        ))}
      </div>
      <p className="mb-6 text-sm text-gray-600 dark:text-gray-400 max-w-3xl">{active.intro}</p>
      {tab === 'rules' && <FinanceRulesTab />}
      {tab === 'suggestions' && <SuggestionsTab />}
      {tab === 'thresholds' && <PlaybookThresholdsTab />}
    </div>
  );
};

export default RuleBook;
